import { useState, useEffect, useContext, useRef } from 'react';
import GlobalErrorBoundary from './ErrorBoundary.jsx';
import { AuthContext, AuthProvider, setOnTokenInvalid } from './context/AuthContext.jsx';
import Capture from './pages/Capture.jsx';
import Tier1Result from './pages/Tier1Result.jsx';
import Tier2Result from './pages/Tier2Result.jsx';
import ReportPage from './pages/ReportPage.jsx';
import InfluencerApply from './pages/InfluencerApply.jsx';
import Login from './pages/Login.jsx';
import RequireAuth from './router/RequireAuth.jsx';
import AdminRequireAuth from './router/AdminRequireAuth.jsx';
import AdminLogin from './pages/AdminLogin.jsx';
import AdminDashboard from './pages/AdminDashboard.jsx';

// 跳中枢登录（保留当前完整 URL 作为 redirect，登录完回跳）。
// local=1 时走本地登录表单（调试用），不跳中枢。
export function redirectToAuthCenter() {
  const useLocalLogin = new URL(window.location.href).searchParams.get('local') === '1';
  if (useLocalLogin) return;
  const target = encodeURIComponent(window.location.href);
  window.location.href = 'https://auth.meijian.top?redirect=' + target;
}

function Router() {
  const { token, loading, validating, hubUnreachable, logout, login } = useContext(AuthContext);
  const [page, setPage] = useState(() => {
    const saved = sessionStorage.getItem('auth_redirect_from');
    const path = window.location.pathname;
    return saved || (path === '/' ? '' : path) || '/report';
  });
  useEffect(() => {
    const onPopState = () => {
      const path = window.location.pathname;
      if (path !== page) setPage(path === '' ? '/report' : path);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [page]);

  // 中枢登录成功后回本端（可能带 ?token=/ ?redirect=，也可能被中枢吞掉 query 只回根路径）：
// 统一从 sessionStorage.auth_redirect_from 还原目标页，清掉 query，以 cookie 重新判定
  const handledCallbackRef = useRef(false);
  useEffect(() => {
    if (handledCallbackRef.current) return;
    const u = new URL(window.location.href);
    const hasToken = u.searchParams.has('token');
    const hasRedirect = u.searchParams.has('redirect');
    const invite = u.searchParams.get('invite');
    const savedFrom = sessionStorage.getItem('auth_redirect_from');
    if (hasToken || hasRedirect || invite || (u.pathname === '/' && !!savedFrom)) {
      handledCallbackRef.current = true;
      if (invite) sessionStorage.setItem('invite_code', invite);
      // 目标页优先取 sessionStorage（中枢可能吞掉 ?redirect= query），否则回落到 /report
      const target = savedFrom ? new URL(savedFrom).pathname || '/report' : '/report';
      sessionStorage.removeItem('auth_redirect_from');
      const clean = new URL(window.location.href);
      clean.search = '';
      setPage(target);
      try {
        const finalPath = clean.pathname + target;
        if (finalPath.startsWith('/') && !finalPath.startsWith('//')) {
          window.history.replaceState(null, '', finalPath);
        } else {
          window.location.href = finalPath;
        }
      } catch (e) {
        console.warn('[App] replaceState failed:', e);
        const finalPath = clean.pathname + target;
        if (finalPath.startsWith('/') && !finalPath.startsWith('//')) {
          window.location.href = finalPath;
        }
      }
      login(); // 重新探测 cookie 登录态
    }
  }, [login]);

  const handleLogout = async () => {
    await logout();
    sessionStorage.removeItem('auth_redirect_from');
    const target = '/report';
    setPage(target);
    try {
      if (target.startsWith('/') && !target.startsWith('//')) {
        window.history.replaceState(null, '', target);
      } else {
        window.location.href = target;
      }
    } catch (e) {
      console.warn('[App] replaceState failed:', e);
      if (target.startsWith('/') && !target.startsWith('//')) {
        window.location.href = target;
      }
    }
  };

  // 无有效登录态：跳中枢登录（?redirect= 当前页）
  // 仅在 validating 完成（登录态探测结束）且确认无登录态时才跳，避免启动瞬间误跳
  // hubUnreachable（中枢 503）时不跳——用户可能已登录，只是中枢暂时不可达
  useEffect(() => {
    if (loading || validating) return;
    if (token) return; // 有有效登录态
    if (hubUnreachable) return; // 中枢不可达，不跳登录
    const path = window.location.pathname;
    const effectivePath = path === '/' ? '' : path;
    if (new URL(window.location.href).searchParams.get('local') === '1') return; // 本地调试
    if (effectivePath.startsWith('/admin')) return; // 管理后台独立鉴权
    if (effectivePath === '/login') return;
    sessionStorage.setItem('auth_redirect_from', window.location.href);
    redirectToAuthCenter(); // 跳中枢根路径，不拼 ?redirect=（中枢前端会吞 query）
  }, [token, loading, validating, hubUnreachable]);

    // 有登录态且落在根路径 → 归一到 /report（选项卡页）
  useEffect(() => {
    if (!token || loading) return;
    const path = window.location.pathname;
    if (path === '/' || path === '') {
      setPage('/report');
      try {
        window.history.replaceState(null, '', '/report');
      } catch (e) {
        console.warn('[App] replaceState failed:', e);
        window.location.href = '/report';
      }
    }
  }, [token, loading]);

  if (loading) return <div className="loading">加载中...</div>;

  if (page === '/admin/login') return <AdminLogin />;
  if (page === '/login') return <Login />;
  if (page === '/admin/dashboard') return <AdminRequireAuth><AdminDashboard /></AdminRequireAuth>;

  // /login 仅作为本地调试（?local=1）入口保留；正常流程会被跳中枢
  const renderPage = () => (
    <RequireAuth
      fallbackPath="/report"
      onNavigate={(path) => {
        setPage(path);
        try {
          if (path.startsWith('/') && !path.startsWith('//')) {
            window.history.replaceState(null, '', path);
          } else {
            window.location.href = path;
          }
        } catch (e) {
          console.warn('[App] replaceState failed:', e);
          if (path.startsWith('/') && !path.startsWith('//')) {
            window.location.href = path;
          }
        }
      }}
    >
      {page === '/capture' && <Capture />}
      {page === '/tier1-result' && <Tier1Result />}
      {page === '/tier2-result' && <Tier2Result />}
      {(page === '/report' || page.startsWith('/report/')) && <ReportPage />}
      {page === '/influencer-apply' && <InfluencerApply />}
    </RequireAuth>
  );

  return renderPage();
}

export default function App() {
  return (
    <GlobalErrorBoundary>
      <AuthProvider>
        <Router />
      </AuthProvider>
    </GlobalErrorBoundary>
  );
}



